import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import {
  ActionCard,
  CheckList,
  DataList,
  Field,
  KeyValue,
  SelectField,
  StatusPill,
  Toggle,
  type CheckItem,
  type DataListColumn,
  type KVRow,
  type StatusTone,
  TextAreaField,
} from "@ozpanel/kit";
import {
  Button,
  ConfirmDialog,
  Section,
  StateView,
  formatRelative,
  useAuth,
  useToast,
} from "@ozpanel/ui";
import { PhasedSteps, type JobStepView } from "./PhasedSteps";

// The cluster's ADMINISTRATIVE surface: identity and the CA, the mesh, joining, secrets,
// updates, recovery, and the record of what has been changed and who has been removed.
//
// ClusterScreen.tsx already covers the observational half — who is a member, can they reach each
// other, are they converged. This is the half you act through, and none of it had ever been
// rendered: 28 documented cluster operations, none reachable from a console, every one of them
// performed with curl during the drills that built them.
//
// Grouped by the QUESTION each answers rather than by URL shape, because during an incident the
// question is what an operator has. "Can this node still be called by its peers" and "who holds
// the CA key" are the same investigation; /cluster/identity and /cluster/ca being separate
// endpoints is an implementation detail.

// ── Identity & the certificate authority ──────────────────────────────────────────────────

/** What /cluster/master reports about THIS node. The endpoint has always answered it; nothing
 *  asked. Every action below that changes who the master is needs it — offering "Claim the master
 *  role" on the node that already holds the key is the interlock failure this codebase keeps
 *  producing: a control shown without the state that governs it. */
interface MasterState {
  am_i_leader: boolean;
  has_master: boolean;
  i_hold_key: boolean;
  node_name: string;
  this_node: string;
  verified: boolean;
  declared_at?: string;
}

interface CA {
  present: boolean;
  can_issue: boolean;
  fingerprint: string;
  issuer_node: string;
  not_before: string;
  not_after: string;
  days_left: number;
  cert_path: string;
  ca_pem?: string;
}
interface Identity {
  fingerprint: string;
  days_left: number;
  not_after: string;
  cert_path: string;
  dns_names: string[] | null;
  ip_addresses: string[] | null;
  holds_ca_key: boolean;
  ca_subject: string;
  ca_days_left: number;
  ca_not_after: string;
}

function IdentityTab() {
  const { client } = useAuth();
  // The import route REQUIRES the key material ({ca_key, ca_cert?, declare?}); the card used to POST
  // nothing and could only ever answer 400. Held in this component only, never stored or echoed.
  const [caKey, setCaKey] = React.useState("");
  const [caCert, setCaCert] = React.useState("");
  const [declare, setDeclare] = React.useState(false);
  const ca = useQuery({
    queryKey: ["cluster-ca"],
    queryFn: () => client.apiFetch<CA>("/cluster/ca"),
    refetchInterval: 60000,
  });
  const master = useQuery({
    queryKey: ["cluster-master"],
    queryFn: () => client.apiFetch<MasterState>("/cluster/master"),
    refetchInterval: 60000,
  });
  const id = useQuery({
    queryKey: ["cluster-identity"],
    queryFn: () => client.apiFetch<Identity>("/cluster/identity"),
    refetchInterval: 60000,
  });

  // Expiry is the whole point of showing these, so it is graded rather than printed. A
  // certificate with 9 days left and one with 300 are not the same fact, and "2027-06-11" makes
  // the reader do that arithmetic themselves — which is exactly when nobody does.
  const expiry = (days: number | undefined, floor: number) =>
    days == null ? (
      <span className="text-muted">unknown</span>
    ) : (
      <StatusPill tone={days < 0 ? "bad" : days < floor ? "warn" : "ok"}>
        {days < 0 ? `expired ${-days}d ago` : `${days} days left`}
      </StatusPill>
    );

  const idRows: KVRow[] = [
    { key: "fingerprint", value: id.data?.fingerprint, label: "Certificate" },
    {
      key: "days_left",
      label: "Validity",
      value: id.data?.days_left,
      render: (v) => expiry(v as number, 14),
      hint: "Renewed automatically by the cluster.identity loop. A node whose identity expires cannot be called by its peers, and cross-node work then fails in ways that look like network faults.",
    },
    { key: "not_after", label: "Expires", value: id.data?.not_after },
    { key: "dns_names", label: "Names", value: id.data?.dns_names ?? [] },
    {
      key: "ip_addresses",
      label: "Addresses",
      value: id.data?.ip_addresses ?? [],
    },
    {
      key: "holds_ca_key",
      label: "Holds the CA key",
      value: id.data?.holds_ca_key,
      goodWhen: true,
      hint: "Only this node can sign a new member's certificate or a binary update. It is not a health property — exactly one node in the cluster should say yes.",
    },
    { key: "cert_path", label: "On disk", value: id.data?.cert_path },
  ];

  const caRows: KVRow[] = [
    { key: "present", label: "Present on this node", value: ca.data?.present },
    {
      key: "can_issue",
      label: "Can issue",
      value: ca.data?.can_issue,
      goodWhen: true,
      hint: "Requires the private key, which lives on one node. A follower holding only the certificate can VERIFY peers but cannot admit a new one.",
    },
    { key: "issuer_node", label: "Held by", value: ca.data?.issuer_node },
    { key: "fingerprint", label: "Fingerprint", value: ca.data?.fingerprint },
    {
      key: "days_left",
      label: "Validity",
      value: ca.data?.days_left,
      render: (v) => expiry(v as number, 180),
      hint: "A CA that expires invalidates every node certificate it signed at once, so it is warned about far earlier than a leaf certificate.",
    },
    { key: "not_after", label: "Expires", value: ca.data?.not_after },
    { key: "cert_path", label: "On disk", value: ca.data?.cert_path },
  ];

  return (
    <div className="space-y-4">
      <Section title="This node's identity" bodyClassName="p-0">
        <StateView
          loading={id.isLoading}
          error={id.error}
          errorContext="the node identity"
        >
          <KeyValue rows={idRows} />
        </StateView>
      </Section>

      <Section title="Cluster certificate authority" bodyClassName="p-0">
        <StateView
          loading={ca.isLoading}
          error={ca.error}
          errorContext="the cluster CA"
        >
          <KeyValue rows={caRows} />
        </StateView>
      </Section>

      <Section title="Actions">
        <div className="space-y-3">
          <ActionCard
            title="Re-issue this node's identity"
            description="Mints a fresh certificate for this node from the cluster CA. Normally unnecessary — the identity loop renews well before expiry — and used when a certificate was revoked, when a node's addresses have changed, or to recover from a botched rename."
            actionLabel="Re-issue"
            danger="medium"
            unavailable={
              ca.data && !ca.data.present
                ? "There is no cluster CA on this node to sign a certificate, so re-issuing cannot succeed. Initialise the CA first, or join this node to a cluster that has one."
                : ca.data && !ca.data.can_issue
                  ? "This node holds the CA certificate but not the key that signs with it, so it cannot issue. Ask the CA holder to re-issue, or import the master key here."
                  : undefined
            }
            confirm={{
              title: "Re-issue this node's identity?",
              body: "The current certificate stops being the one peers know. Cross-node calls use the new one immediately; nothing needs restarting, but a peer that cannot reach the CA will not learn of it until it can.",
            }}
            run={() =>
              client.apiFetch("/cluster/identity/issue", { method: "POST" })
            }
            invalidates={[["cluster-identity"], ["cluster-ca"]]}
          />
          <ActionCard
            title="Initialise the cluster CA"
            description="Creates the certificate authority this cluster's node identities are signed by. Run once, on the first node, before any other node joins."
            actionLabel="Initialise"
            danger="high"
            unavailable={
              ca.data?.present
                ? "A CA already exists for this cluster. Re-initialising would orphan every certificate it has signed — every node would refuse every peer — so this is offered only on a node that has none."
                : undefined
            }
            confirm={{
              title: "Initialise a new cluster CA?",
              body: "Only do this on the FIRST node of a new cluster. If any node already holds a CA, this creates a second, mutually unrecognised authority.",
              confirmLabel: "Initialise CA",
            }}
            run={() => client.apiFetch("/cluster/ca/init", { method: "POST" })}
            invalidates={[["cluster-ca"]]}
          />
          <ActionCard
            title="Claim the master role"
            description="Declares this node the holder of the cluster master key — the node that signs updates and admits members. Used when the previous master has been lost and a survivor must take over."
            actionLabel="Claim"
            danger="high"
            unavailable={
              // node10 IS the master and this was offered anyway, with a full confirmation
              // dialog, on the node that already holds the key. /cluster/master has always
              // answered i_hold_key; nothing asked it. Claiming again is not harmless: it
              // re-declares the role and the note beside it is written for a node that does
              // not hold it.
              master.data?.i_hold_key
                ? `This node (${master.data.this_node}) already holds the master key — it was declared ${master.data.declared_at ? new Date(master.data.declared_at).toLocaleString() : "earlier"}. There is nothing to claim.`
                : master.data?.has_master
                  ? `${master.data.node_name} is the master. Claiming here makes a SECOND node believe it holds the role: the two will sign with different keys and disagree about who may join. Take the role away from ${master.data.node_name} first, or import its key instead.`
                  : undefined
            }
            confirm={{
              title: "Claim the master role on this node?",
              body: "Two nodes both believing they are master will sign with different keys and disagree about who may join. Only claim when the previous master is definitively gone.",
              confirmLabel: "Claim master",
            }}
            run={() =>
              client.apiFetch("/cluster/master/claim", { method: "POST" })
            }
            invalidates={[["cluster-master"], ["cluster-ca"]]}
          />
          <div className="space-y-2 rounded-lg border border-line p-3">
            {!master.data?.i_hold_key && (
              <div className="grid gap-2">
                <TextAreaField
                  label="The cluster CA private key (PEM) — cluster-ca.key on a node that holds it"
                  value={caKey}
                  onChange={setCaKey}
                  rows={4}
                  mono
                  secret
                  placeholder="-----BEGIN EC PRIVATE KEY-----"
                />
                <TextAreaField
                  label="The CA certificate (PEM) — optional: without it the key is checked against the certificate already on this node"
                  value={caCert}
                  onChange={setCaCert}
                  rows={3}
                  mono
                  placeholder="-----BEGIN CERTIFICATE-----"
                />
                <Toggle
                  checked={declare}
                  onChange={setDeclare}
                  label="Declare this node master once the key is imported"
                  help="The claim is refused unless the current master is unreachable and this node is the one already named next in the succession — a master that answers keeps the role."
                />
              </div>
            )}
            <ActionCard
              title="Import the master key"
              description="Places the cluster CA key on THIS node so it can sign in the master's place. The key is checked against the CA certificate (a signature made with it must verify) before anything is written. Import moves the ability; claiming (above, or the option here) asserts the role."
              actionLabel="Import"
              danger="high"
              unavailable={
                master.data?.i_hold_key
                  ? "This node already holds the master key. Importing would replace the key every certificate it has signed was signed WITH — peers that trust the current one would refuse it."
                  : !caKey.includes("PRIVATE KEY")
                    ? "Paste the CA private key (PEM) above first."
                    : undefined
              }
              confirm={{
                title: "Import master key material?",
                body: declare
                  ? "The key is written here and this node then claims the master role — refused if the current master still answers."
                  : "This node becomes able to sign updates and admit members. The node it came from is not demoted.",
                confirmLabel: "Import",
              }}
              run={async () => {
                const r = await client.apiFetch("/cluster/master/import", {
                  method: "POST",
                  body: JSON.stringify({ ca_key: caKey, ca_cert: caCert.trim() || undefined, declare }),
                });
                setCaKey("");
                setCaCert("");
                return r;
              }}
              invalidates={[["cluster-master"], ["cluster-ca"]]}
            />
          </div>
        </div>
      </Section>
    </div>
  );
}

// ── Mesh ──────────────────────────────────────────────────────────────────────────────────

interface Mesh {
  interface_up: boolean;
  cidr: string;
  self: { name?: string; vpn_ip?: string } | null;
  nodes: { name: string; vpn_ip: string; reachable?: boolean }[] | null;
  // An array of peer NAMES, not a count. Declaring it a number was harmless only because
  // KeyValue happens to render arrays; the label still promised a number.
  reachable_peers: string[] | null;
  replication_network: string;
}

function MeshTab() {
  const { client } = useAuth();
  const [inviteName, setInviteName] = React.useState("");
  const [defaultNet, setDefaultNet] = React.useState("");
  const [svcNet, setSvcNet] = React.useState<Record<string, string>>({});
  const masterQ = useQuery({
    queryKey: ["cluster-master"],
    queryFn: () => client.apiFetch<MasterState>("/cluster/master"),
  });
  const registry = useQuery({
    queryKey: ["cluster-nodes"],
    queryFn: () => client.apiFetch<{ items: { name: string; self: boolean }[] | null }>("/cluster/nodes"),
  });
  const mesh = useQuery({
    queryKey: ["cluster-mesh"],
    queryFn: () => client.apiFetch<Mesh>("/cluster/mesh"),
    refetchInterval: 30000,
  });
  const net = useQuery({
    queryKey: ["cluster-network"],
    queryFn: () =>
      // A LIST of services, not a map keyed by name.
      client.apiFetch<{
        replication_network: string;
        services:
          | {
              key?: string;
              service?: string;
              detail?: string;
              note?: string;
              is_default?: boolean | null;
              on_switch?: boolean;
              network?: string | null;
            }[]
          | null;
      }>("/cluster/network"),
    refetchInterval: 60000,
  });

  const rows: KVRow[] = [
    {
      key: "interface_up",
      label: "Mesh interface",
      value: mesh.data?.interface_up,
      goodWhen: true,
      hint: "Replication, GlusterFS and every peer call ride this. It carries no traffic a public address can substitute for.",
    },
    { key: "cidr", label: "Mesh range", value: mesh.data?.cidr },
    {
      key: "vpn_ip",
      label: "This node's mesh address",
      value: mesh.data?.self?.vpn_ip,
    },
    {
      key: "reachable_peers",
      label: "Reachable peers",
      value: mesh.data?.reachable_peers ?? [],
      hint: "A peer that is unreachable over the mesh but fine publicly still cannot replicate — the two paths fail independently and for different reasons.",
    },
    {
      key: "replication_network",
      label: "Replication network",
      value: net.data?.replication_network ?? mesh.data?.replication_network,
      hint: "Which network the database and storage layers are told to use. It must be the mesh: a service pointed at a public address leaves the cluster's traffic unencrypted and breaks when a floating IP moves.",
    },
  ];

  const columns: DataListColumn<{
    name: string;
    vpn_ip: string;
    reachable?: boolean;
  }>[] = [
    { key: "name", label: "Node", sortable: true },
    {
      key: "vpn_ip",
      label: "Mesh address",
      sortable: true,
      render: (n) => <span className="font-mono text-[12px]">{n.vpn_ip}</span>,
    },
    {
      key: "reachable",
      label: "Reachable",
      render: (n) =>
        n.reachable == null ? (
          // Not asked is not the same as unreachable, and painting it red invents a fault.
          <span className="text-muted">not measured</span>
        ) : (
          <StatusPill tone={n.reachable ? "ok" : "bad"}>
            {n.reachable ? "yes" : "no"}
          </StatusPill>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <Section title="Mesh" bodyClassName="p-0">
        <StateView
          loading={mesh.isLoading}
          error={mesh.error}
          errorContext="the mesh"
        >
          <KeyValue rows={rows} />
        </StateView>
      </Section>

      <Section title="Members on the mesh" bodyClassName="p-0">
        <div className="p-4">
          <DataList
            items={mesh.data?.nodes ?? []}
            columns={columns}
            getRowKey={(n) => n.name}
            loading={mesh.isLoading}
            error={mesh.error}
            errorContext="the mesh member list"
            emptyMessage="No mesh members."
          />
        </div>
      </Section>

      <Section title="Actions">
        <div className="space-y-3">
          <ActionCard
            title="Set up the mesh on this node"
            unavailable={
              // Already meshed and running is not a state to "set up" again — the step is
              // idempotent, but offering it implies something is missing when nothing is.
              mesh.data?.interface_up && mesh.data?.self?.vpn_ip
                ? "This node is already on the mesh (" +
                  mesh.data.self.vpn_ip +
                  ") and its interface is up."
                : undefined
            }
            description="Builds this node's mesh configuration and brings the interface up. Idempotent — safe to re-run on a node that is already meshed, where it repairs configuration rather than replacing it."
            actionLabel="Set up"
            danger="medium"
            confirm={{
              title: "Set up the mesh on this node?",
              body: "The interface is reconfigured and restarted. In-flight replication and GlusterFS traffic will pause for the moment it takes to come back.",
            }}
            run={() =>
              client.apiFetch("/cluster/mesh/setup", { method: "POST" })
            }
            invalidates={[["cluster-mesh"]]}
          />
          <div className="space-y-2 rounded-lg border border-line p-3">
            <div className="max-w-xs">
              <SelectField
                label="Registered node to invite"
                value={inviteName}
                onChange={setInviteName}
                options={[
                  { value: "", label: "— choose a node —" },
                  ...(registry.data?.items ?? [])
                    .filter((n) => !n.self)
                    .map((n) => {
                      const vpn = (mesh.data?.nodes ?? []).find((m) => m.name === n.name)?.vpn_ip;
                      return { value: n.name, label: vpn ? `${n.name} (already ${vpn})` : n.name };
                    }),
                ]}
              />
            </div>
            <ActionCard
              title="Invite a node to the mesh"
              description="Allocates the node's VPN address and mints a ONE-TIME tinc invitation for it — the bundle shown below is what the node presents (POST /cluster/mesh/join on that node). Only for joining a node to the mesh by hand: Add a node does this itself. The node must already be registered."
              actionLabel="Create invitation"
              danger="medium"
              unavailable={
                masterQ.data && !masterQ.data.am_i_leader
                  ? `Only the master issues invitations${masterQ.data.node_name ? ` — currently ${masterQ.data.node_name}` : ""}.`
                  : !inviteName
                    ? "Choose the registered node to invite."
                    : undefined
              }
              confirm={{
                title: `Invite ${inviteName} to the mesh?`,
                body: "Any stale host entry for this name is cleared and, if tincd still holds the name in memory, tincd is restarted so it forgets it — peers on the mesh blip for a moment.",
                confirmLabel: "Invite",
              }}
              run={() => client.apiFetch("/cluster/mesh/invite", { method: "POST", body: JSON.stringify({ name: inviteName }) })}
              invalidates={[["cluster-mesh"]]}
            />
          </div>
        </div>
      </Section>

      {/* What /cluster/mesh/transport ACTUALLY does: set the network every inter-node service with no
          choice of its own uses. The card here used to call it "Rebuild mesh transport", describe a
          re-derivation after an address change, and send no network — a 400 from a route that would,
          given one, have moved the database's replication between the mesh and the public internet. */}
      <Section
        title="Which network each service reaches its peers over"
        hint={net.data ? `cluster default: ${net.data.replication_network}` : undefined}
        bodyClassName="p-0"
      >
        <StateView loading={net.isLoading} error={net.error} errorContext="the per-service network">
          <div className="divide-y divide-line">
            {(net.data?.services ?? []).map((sv, i) => (
              <div key={sv.key ?? `own-${i}`} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-[12.5px]">
                <div className="min-w-0 flex-1">
                  <div>{sv.service ?? sv.key}</div>
                  <div className="text-[11.5px] leading-relaxed text-muted">{sv.detail ?? sv.note}</div>
                </div>
                {sv.on_switch && sv.key ? (
                  <>
                    <StatusPill tone={sv.network == null ? "warn" : sv.network === "mesh" ? "ok" : "neutral"}>
                      {sv.network == null ? "unread" : `set: ${sv.network}${sv.is_default ? " (default)" : ""}`}
                    </StatusPill>
                    <SelectField
                      label="Change to"
                      value={svcNet[sv.key] ?? ""}
                      onChange={(v) => setSvcNet((x) => ({ ...x, [sv.key as string]: v }))}
                      options={[
                        { value: "", label: "change to…" },
                        { value: "mesh", label: "mesh" },
                        { value: "public", label: "public" },
                      ]}
                    />
                  </>
                ) : (
                  <span className="text-[11.5px] text-muted">{sv.network}</span>
                )}
              </div>
            ))}
          </div>
        </StateView>
        <div className="space-y-3 border-t border-line p-4">
          {Object.entries(svcNet)
            .filter(([, v]) => v)
            .map(([key, v]) => (
              <ActionCard
                key={key}
                title={`Move ${key} to the ${v} network`}
                description={`Records ${key}'s peer network as "${v}", re-derives it on this node now and journals the change so every peer re-derives it too. It changes the firewall: on "mesh" its ports are accepted from VPN addresses only.`}
                actionLabel="Apply"
                danger="high"
                confirm={{
                  title: `Move ${key} to the ${v} network?`,
                  body: "A firewall and peer-address change on every node. A node that cannot reach its peers on the chosen network stops replicating — confirm the mesh (or the public path) works between every pair first.",
                  confirmLabel: "Apply",
                }}
                run={async () => {
                  const r = await client.apiFetch(`/cluster/network/${encodeURIComponent(key)}/transport`, {
                    method: "POST",
                    body: JSON.stringify({ network: v }),
                  });
                  setSvcNet((x) => ({ ...x, [key]: "" }));
                  return r;
                }}
                invalidates={[["cluster-network"], ["cluster-mesh"], ["cluster-connectivity"]]}
              />
            ))}
          <div className="max-w-xs">
            <SelectField
              label="Cluster default network"
              value={defaultNet}
              onChange={setDefaultNet}
              help="Applies to every service above that has no choice of its own."
              options={[
                { value: "", label: "— unchanged —" },
                { value: "mesh", label: "mesh (VPN addresses only)" },
                { value: "public", label: "public addresses" },
              ]}
            />
          </div>
          <ActionCard
            title="Set the cluster default network"
            description="Records the default, re-derives every service without its own choice on this node, and journals it so every peer does the same. The response says only that the re-derivation was called — each service's own status says whether it now uses the network."
            actionLabel="Apply"
            danger="high"
            unavailable={!defaultNet ? "Choose mesh or public first." : net.data?.replication_network === defaultNet ? `The default is already ${defaultNet}.` : undefined}
            confirm={{
              title: `Set the cluster default network to ${defaultNet}?`,
              body: "Galera, PostgreSQL, Redis and storage traffic without an explicit choice move with it, and their firewall rules are re-derived on every node. Never change this without the target network proven between every pair of nodes.",
              confirmLabel: "Apply",
            }}
            run={() => client.apiFetch("/cluster/mesh/transport", { method: "POST", body: JSON.stringify({ network: defaultNet }) })}
            invalidates={[["cluster-network"], ["cluster-mesh"], ["cluster-connectivity"]]}
          />
        </div>
      </Section>
    </div>
  );
}

// ── Joining: what must be true, and what has been done ────────────────────────────────────

interface Requirement {
  step: string;
  ok: boolean;
  declared: string;
}
interface Step {
  name: string;
  label: string;
  phase: number;
  implemented: boolean;
}
interface Membership {
  node: string;
  uuid: string;
  evicted: boolean;
  certificates?: Record<string, unknown> | null;
}
/** A join/floating-IP job as recorded in cluster_join_jobs. Listed by GET /cluster/join. */
interface JoinJob {
  job_id: string;
  node: string;
  status: string;
  started_at: string;
  completed_at?: string | null;
}
/** One step of a running job. The server has always recorded `detail` — the ASSERT lines and the
 *  tail of the command's own output — and nothing rendered it until now: the console showed a job
 *  only as a single status, with no way to see which step it was on or what that step said. During
 *  a floating-IP run that is the whole picture — the route flips mid-job, and "running" says
 *  nothing about whether it is about to lose the connection or already has. */
interface JobStep {
  name: string;
  phase: number;
  label: string;
  status: "pending" | "running" | "done" | "skipped" | "failed";
  detail?: string;
}
interface JobDetailData {
  job_id: string;
  node: string;
  status: string;
  steps: JobStep[];
  error?: string;
}

/** The live detail for one job, expanded beneath its row. Polls while the job is still live and
 *  stops on a terminal status — an operator who leaves the tab open on a finished job should not
 *  poll it forever. `floating_*` ids are served under their own path (they carry the hub
 *  credentials in memory for the job's life and are never persisted, so the read path differs). */
function JobDetail({ job }: { job: JoinJob }) {
  const { client } = useAuth();
  const qc = useQueryClient();
  const path = job.job_id.startsWith("floating_")
    ? `/cluster/floating-ip/${encodeURIComponent(job.job_id)}`
    : `/cluster/join/${encodeURIComponent(job.job_id)}`;
  const detail = useQuery({
    queryKey: ["cluster-job", job.job_id],
    queryFn: () => client.apiFetch<JobDetailData>(path),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "running" || s === "pending" ? 2000 : false;
    },
  });
  const resume = useMutation({
    mutationFn: () =>
      client.apiFetch(
        `/cluster/bootstrap/${encodeURIComponent(job.job_id)}/resume`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cluster-job", job.job_id] });
      qc.invalidateQueries({ queryKey: ["cluster-jobs"] });
    },
  });
  const d = detail.data;
  const tone = (s: string): StatusTone =>
    s === "done"
      ? "ok"
      : s === "failed"
        ? "bad"
        : s === "running"
          ? "accent"
          : "neutral";
  return (
    <StateView
      loading={detail.isLoading}
      error={detail.error}
      errorContext="job detail"
    >
      <div className="space-y-2 py-2">
        {d?.error && (
          <div className="rounded border border-bad px-3 py-2 text-[12.5px] text-bad">
            {d.error}
          </div>
        )}
        {(d?.steps ?? []).map((s) => (
          <div
            key={s.name}
            className="rounded border border-line px-3 py-2 text-[12.5px]"
          >
            <div className="flex items-center gap-2">
              <StatusPill tone={tone(s.status)}>{s.status}</StatusPill>
              <span className="font-mono">{s.name}</span>
              <span className="text-muted">{s.label}</span>
            </div>
            {s.detail && (
              <pre className="mt-1.5 max-h-56 overflow-auto whitespace-pre-wrap break-words rounded bg-black/30 p-2 text-[11px]">
                {s.detail}
              </pre>
            )}
          </div>
        ))}
        {/* The right remedy depends on the KIND of job. Only a bootstrap can be resumed — the server
            refuses the others (their credentials lived in memory only), and offering "Resume" on them
            was a button that could only answer 400. */}
        {d?.status === "failed" && job.job_id.startsWith("bootstrap_") && (
          <ActionCard
            title="Resume from the failed step"
            description={`Re-runs ${d.node}'s bootstrap starting at the step that failed; earlier steps are not repeated. A job of another node is relayed to that node, whose panel must be running.`}
            actionLabel="Resume"
            run={() => resume.mutateAsync()}
          />
        )}
        {d?.status === "failed" && job.job_id.startsWith("join_") && (
          <div className="rounded border border-line px-3 py-2 text-[12.5px] leading-relaxed">
            A join cannot be resumed — its SSH passwords were held in memory only.{" "}
            <Link className="text-accent hover:underline" to={`/nodes/add?job=${encodeURIComponent(job.job_id)}`}>
              Open it under Add a node
            </Link>{" "}
            to see both halves and send it again, or resume the joiner's own bootstrap if that is what failed.
          </div>
        )}
        {d?.status === "failed" && job.job_id.startsWith("floating_") && (
          <div className="rounded border border-line px-3 py-2 text-[12.5px] leading-relaxed">
            A floating-IP job cannot be resumed — its hub credentials were held in memory only. Start it again from the
            Floating IP tab on {d.node}'s console; every step is idempotent.
          </div>
        )}
      </div>
    </StateView>
  );
}

function JoinTab() {
  const { client } = useAuth();
  const [expandedJob, setExpandedJob] = React.useState<string | null>(null);
  const jobs = useQuery({
    queryKey: ["cluster-jobs"],
    queryFn: () =>
      client.apiFetch<{ items: JoinJob[]; total: number }>("/cluster/join"),
    // Slower than a job's own 2s poll: this list only needs to notice a NEW job appearing, and
    // the expanded detail below already tracks a running one at the pace that matters.
    refetchInterval: 15000,
  });
  const reqs = useQuery({
    queryKey: ["cluster-requirements"],
    queryFn: () =>
      client.apiFetch<{
        items: Requirement[] | null;
        incomplete: number;
        total: number;
        note?: string;
      }>("/cluster/requirements"),
  });
  const steps = useQuery({
    queryKey: ["cluster-steps"],
    queryFn: () =>
      client.apiFetch<{
        items: Step[] | null;
        pending: string[] | null;
        pending_total: number;
        total: number;
      }>("/cluster/steps"),
  });
  const mem = useQuery({
    queryKey: ["cluster-membership"],
    queryFn: () => client.apiFetch<Membership>("/cluster/membership"),
  });
  // A GET (?with_tinc=&with_first_login=) — the card here used to POST it and got 405.
  const plan = useQuery({
    queryKey: ["cluster-join-plan", false],
    queryFn: () =>
      client.apiFetch<{ steps: JobStepView[]; total: number; phases: Record<string, string> }>("/cluster/join/plan"),
  });

  const reqItems: CheckItem[] = (reqs.data?.items ?? []).map((r) => ({
    key: r.step,
    label: r.step,
    state: r.ok ? "ok" : "fail",
    detail: r.declared || r.declared,
  }));

  // A declared step with no implementation is a NO-OP that reads as complete — the exact defect
  // glusterfs_peer was. It is shown as pending rather than passing, because a plan that looks
  // finished is worse than one that admits a gap.
  const stepItems: CheckItem[] = (steps.data?.items ?? []).map((s) => ({
    key: s.name,
    label: `${s.label}`,
    state: s.implemented ? "ok" : "pending",
    detail: `phase ${s.phase} · ${s.name}`,
    because: s.implemented
      ? undefined
      : "Declared but not implemented on this build — running it would change nothing while reporting success.",
  }));

  return (
    <div className="space-y-4">
      <Section
        title="Requirements"
        hint={
          reqs.data
            ? `${reqs.data.total - reqs.data.incomplete}/${reqs.data.total} satisfied`
            : undefined
        }
        bodyClassName="p-0"
      >
        <StateView
          loading={reqs.isLoading}
          error={reqs.error}
          errorContext="the cluster requirements"
        >
          <CheckList
            items={reqItems}
            summarise
            emptyMessage="No requirements declared."
          />
        </StateView>
      </Section>

      <Section
        title="Join steps"
        hint={
          steps.data
            ? `${steps.data.total} declared · ${steps.data.pending_total} pending`
            : undefined
        }
        bodyClassName="p-0"
      >
        <StateView
          loading={steps.isLoading}
          error={steps.error}
          errorContext="the join steps"
        >
          <CheckList
            items={stepItems}
            summarise
            emptyMessage="No steps declared."
          />
        </StateView>
      </Section>

      <RunOneStep steps={(steps.data?.items ?? []).filter((s) => s.implemented)} />

      <Section title="This node's membership" bodyClassName="p-0">
        <StateView
          loading={mem.isLoading}
          error={mem.error}
          errorContext="membership"
        >
          <KeyValue
            rows={[
              { key: "node", label: "Node", value: mem.data?.node },
              { key: "uuid", label: "UUID", value: mem.data?.uuid },
              {
                key: "evicted",
                label: "Evicted",
                value: mem.data?.evicted,
                goodWhen: false,
                hint: "A tombstoned node refuses to re-register itself, which is what stops an evicted machine quietly rejoining after a reboot.",
              },
            ]}
          />
        </StateView>
      </Section>

      <Section
        title="Add a node"
        actions={
          <Button size="sm" variant="accent" asChild>
            <Link to="/nodes/add">Add a node</Link>
          </Button>
        }
      >
        <p className="text-[12.5px] leading-relaxed text-muted">
          Preflight a bare machine over SSH, send the join from the master, and watch the master's steps and then the
          new node's own bootstrap. What that bootstrap runs:
        </p>
        <StateView loading={plan.isLoading} error={plan.error} errorContext="the join plan">
          {plan.data && (
            <details className="mt-2">
              <summary className="cursor-pointer text-[12px] text-muted">{plan.data.total} steps</summary>
              <div className="mt-2">
                <PhasedSteps
                  steps={plan.data.steps.map((st) => ({ ...st, status: "pending" }))}
                  phases={Object.fromEntries(Object.entries(plan.data.phases ?? {}).map(([k, v]) => [Number(k), v]))}
                />
              </div>
            </details>
          )}
        </StateView>
      </Section>

      <Section title="Actions">
        <div className="space-y-3">
          <ActionCard
            title="Run the bootstrap job"
            description="Runs this node's recorded bootstrap steps, resuming from the first that has not completed. Safe to re-run: every step is idempotent and a completed one reports its state without acting."
            actionLabel="Bootstrap"
            danger="medium"
            confirm={{
              title: "Run bootstrap on this node?",
              body: "Steps are executed in order from the first incomplete one. This installs and reconfigures host services; it is the same path a fresh node takes.",
            }}
            run={() =>
              client.apiFetch("/cluster/bootstrap", { method: "POST" })
            }
            invalidates={[["cluster-steps"], ["cluster-requirements"]]}
          />
        </div>
      </Section>

      <Section title="Join jobs" bodyClassName="p-0">
        <DataList<JoinJob>
          items={jobs.data?.items ?? []}
          loading={jobs.isLoading}
          error={jobs.error}
          errorContext="join jobs"
          getRowKey={(j) => j.job_id}
          columns={[
            { key: "job_id", label: "Job", className: "font-mono text-[12px]" },
            { key: "node", label: "Node" },
            {
              key: "status",
              label: "Status",
              render: (j) => (
                <StatusPill
                  tone={
                    j.status === "done"
                      ? "ok"
                      : j.status === "failed"
                        ? "bad"
                        : j.status === "running"
                          ? "accent"
                          : "neutral"
                  }
                >
                  {j.status}
                </StatusPill>
              ),
            },
            {
              key: "started_at",
              label: "Started",
              render: (j) => formatRelative(j.started_at),
            },
          ]}
          onRowClick={(j) =>
            setExpandedJob(expandedJob === j.job_id ? null : j.job_id)
          }
          expandedKey={expandedJob}
          renderExpanded={(j) => <JobDetail job={j} />}
          emptyMessage="No join or floating-IP jobs recorded."
        />
      </Section>
    </div>
  );
}

// ── Secrets ───────────────────────────────────────────────────────────────────────────────

interface SecretSpec {
  name: string;
  scope: string;
  why: string;
  present?: boolean;
  editable?: boolean;
}

interface RolloutNode {
  node: string;
  sha256_prefix: string;
  version: number;
  applied: boolean;
  retired: boolean;
  detail?: string;
  measured_at: string;
}

type SecretRolloutData = {
  version: number;
  sha256_prefix?: string;
  set_by?: string;
  set_at?: string;
  nodes: RolloutNode[] | null;
};

// RolloutTable is where each node is in a secret change — the same record for file secrets and the Garage key.
function RolloutTable({
  nodes,
  version,
}: {
  nodes: RolloutNode[];
  version: number;
}) {
  if (nodes.length === 0) return null;
  return (
    <table className="mt-2 w-full text-[12px]">
      <thead>
        <tr className="border-b border-line text-left text-[11px] uppercase text-faint">
          <th className="py-1 pr-2">Node</th>
          <th className="py-1 pr-2">Version</th>
          <th className="py-1 pr-2">State</th>
          <th className="py-1">Detail</th>
        </tr>
      </thead>
      <tbody>
        {nodes.map((n) => {
          const switched = n.applied && n.version === version;
          return (
            <tr
              key={n.node}
              className="border-b border-line align-top last:border-0"
            >
              <td className="py-1 pr-2 font-mono">{n.node}</td>
              <td className="py-1 pr-2">{n.version}</td>
              <td className="py-1 pr-2">
                <StatusPill
                  tone={n.retired ? "ok" : switched ? "accent" : "warn"}
                >
                  {n.retired ? "done" : switched ? "switched" : "waiting"}
                </StatusPill>
              </td>
              <td className="py-1 text-muted">{n.detail}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// EditSecret changes a cluster secret cluster-wide (PUT /cluster/secrets/{name}) and shows the rollout:
// every node pulls the value, makes it accepted, commits it, moves its consumers, then retires the old one.
function EditSecret({ name, onClose }: { name: string; onClose: () => void }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const [value, setValue] = React.useState("");
  const [started, setStarted] = React.useState(false);
  const rollout = useQuery({
    queryKey: ["secret-rollout", name],
    queryFn: () =>
      client.apiFetch<SecretRolloutData>(
        `/cluster/secrets/${encodeURIComponent(name)}/rollout`,
      ),
    refetchInterval: started ? 5000 : false,
  });
  const generate = () => {
    const a = new Uint8Array(24);
    crypto.getRandomValues(a);
    setValue(Array.from(a, (b) => b.toString(16).padStart(2, "0")).join(""));
  };
  const save = async () => {
    try {
      await client.apiFetch(`/cluster/secrets/${encodeURIComponent(name)}`, {
        method: "PUT",
        body: JSON.stringify({ value }),
      });
      setStarted(true);
      setValue("");
      notify({
        kind: "success",
        title: `${name}: new value set — rolling out to every node`,
      });
      void rollout.refetch();
    } catch (e) {
      notify({
        kind: "error",
        title: `Could not change ${name}`,
        body: e instanceof Error ? e.message : String(e),
      });
    }
  };
  const r = rollout.data;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-xl border border-line bg-surface p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[14px] font-medium">Change {name}</div>
        <p className="mt-1 text-[12px] leading-relaxed text-muted">
          Every node pulls the new value, makes it accepted without moving
          anything, then switches what uses it. The old value keeps working
          until every node has switched, and is retired only then — nobody is
          cut off mid-change.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            className="min-w-[18rem] flex-1 rounded border border-line bg-surface px-2 py-1.5 font-mono text-[12.5px]"
            placeholder="new value (16+ characters, no spaces or quotes)"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="new-password"
          />
          <Button size="sm" variant="ghost" onClick={generate}>
            Generate
          </Button>
          <Button
            size="sm"
            variant="accent"
            disabled={value.length < 16}
            onClick={() => void save()}
          >
            Set cluster-wide
          </Button>
        </div>
        <div className="mt-4 text-[12px] text-muted">
          {r && r.version > 0
            ? `Desired version ${r.version} (${r.sha256_prefix}) — set by ${r.set_by} at ${r.set_at}`
            : "Never changed from the console."}
        </div>
        <RolloutTable nodes={r?.nodes ?? []} version={r?.version ?? 0} />
        <div className="mt-4 flex justify-end">
          <Button size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

// RevealSecret shows one on-disk secret of the node that answers — masked until clicked.
function RevealSecret({ name }: { name: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const [shown, setShown] = React.useState<{
    value: string;
    sha: string;
    node: string;
  } | null>(null);
  const [busy, setBusy] = React.useState(false);
  const reveal = async () => {
    setBusy(true);
    try {
      const r = await client.apiFetch<{
        value: string;
        sha256_prefix: string;
        node: string;
      }>(`/secrets/files/${encodeURIComponent(name)}/reveal`, {
        method: "POST",
        body: "{}",
      });
      setShown({ value: r.value, sha: r.sha256_prefix, node: r.node });
    } catch (e) {
      notify({
        kind: "error",
        title: `Could not reveal ${name}`,
        body: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };
  if (!shown) {
    return (
      <span className="flex items-center gap-2">
        <span className="font-mono text-[12px] text-faint">••••••••••••</span>
        <Button size="sm" variant="ghost" disabled={busy} onClick={reveal}>
          Reveal
        </Button>
      </span>
    );
  }
  return (
    <span className="flex max-w-[28rem] flex-wrap items-center gap-2">
      <code className="break-all rounded border border-line bg-bg2 px-1.5 py-0.5 text-[11.5px]">
        {shown.value}
      </code>
      <span
        className="text-[11px] text-faint"
        title="sha256 prefix — compare across nodes"
      >
        {shown.node} · {shown.sha}
      </span>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => void navigator.clipboard?.writeText(shown.value)}
      >
        Copy
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setShown(null)}>
        Hide
      </Button>
    </span>
  );
}

interface GarageKey {
  accessKeyId: string;
  name: string;
  created?: string;
  expiration?: string | null;
  expired: boolean;
  buckets:
    | {
        id: string;
        globalAliases: string[];
        permissions: { read: boolean; write: boolean; owner: boolean };
      }[]
    | null;
  permissions: { createBucket: boolean };
}

// GarageKeys — the S3 keys that can reach the object store (JuiceFS uses ozjuicefs).
function GarageKeys() {
  const { client } = useAuth();
  const { notify } = useToast();
  const q = useQuery({
    queryKey: ["garage-s3-keys"],
    queryFn: () =>
      client.apiFetch<{ keys: GarageKey[] | null; note?: string }>(
        "/garage/s3-keys",
      ),
    retry: false,
  });
  const [secret, setSecret] = React.useState<Record<string, string>>({});
  const reveal = async (id: string) => {
    try {
      const r = await client.apiFetch<{ secretAccessKey?: string }>(
        `/garage/s3-keys/${encodeURIComponent(id)}/reveal`,
        {
          method: "POST",
          body: "{}",
        },
      );
      setSecret((m) => ({ ...m, [id]: r.secretAccessKey ?? "" }));
    } catch (e) {
      notify({
        kind: "error",
        title: "Could not reveal the key",
        body: e instanceof Error ? e.message : String(e),
      });
    }
  };
  const rollout = useQuery({
    queryKey: ["secret-rollout", "garage_s3_key"],
    queryFn: () =>
      client.apiFetch<SecretRolloutData>(
        "/cluster/secrets/garage_s3_key/rollout",
      ),
    refetchInterval: 10000,
  });
  const rotating = (rollout.data?.nodes ?? []).some((n) => !n.retired);
  const rotate = async () => {
    if (
      !window.confirm(
        "Replace the Garage key JuiceFS uses? A second key is created, every node's mounts move onto it live, and the old key is deleted once every node has proven it writes with the new one.",
      )
    )
      return;
    try {
      await client.apiFetch("/juicefs/s3-key/rotate", {
        method: "POST",
        body: "{}",
      });
      notify({
        kind: "success",
        title:
          "Key replacement started — every node moves over within a few minutes",
      });
      void rollout.refetch();
      void q.refetch();
    } catch (e) {
      notify({
        kind: "error",
        title: "Could not start the key replacement",
        body: e instanceof Error ? e.message : String(e),
      });
    }
  };
  return (
    <Section
      title="Object store (Garage) S3 keys"
      hint={q.data?.keys ? String(q.data.keys.length) : undefined}
      bodyClassName="p-0"
    >
      <div className="space-y-3 p-4">
        {q.error && (
          <p className="text-[12.5px] text-crit">
            {String((q.error as Error).message)}
          </p>
        )}
        {q.data?.note && (
          <p className="text-[12px] text-muted">{q.data.note}</p>
        )}
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <Button
            size="sm"
            variant="soft"
            disabled={rotating}
            onClick={() => void rotate()}
          >
            Replace JuiceFS key
          </Button>
          <span className="text-muted">
            {rollout.data && rollout.data.version > 0
              ? `Replacement #${rollout.data.version} by ${rollout.data.set_by} at ${rollout.data.set_at}${rotating ? " — in progress" : " — done"}`
              : "Never replaced from the console."}
          </span>
        </div>
        <RolloutTable
          nodes={rollout.data?.nodes ?? []}
          version={rollout.data?.version ?? 0}
        />
        {(q.data?.keys ?? []).map((k) => (
          <div
            key={k.accessKeyId}
            className="rounded-lg border border-line p-3 text-[12.5px]"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{k.name || "(unnamed)"}</span>
              <code className="text-[12px]">{k.accessKeyId}</code>
              {k.expired && <StatusPill tone="bad">expired</StatusPill>}
              <span className="text-muted">
                {k.created ? `created ${k.created.slice(0, 10)}` : ""}
                {k.expiration
                  ? ` · expires ${k.expiration.slice(0, 10)}`
                  : " · never expires"}
                {k.permissions?.createBucket ? " · may create buckets" : ""}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {(k.buckets ?? []).map((b) => (
                <span
                  key={b.id}
                  className="rounded border border-line bg-bg2 px-1.5 py-0.5 font-mono text-[11px]"
                >
                  {(b.globalAliases ?? [])[0] ?? b.id.slice(0, 12)}:{" "}
                  {[
                    b.permissions.read && "read",
                    b.permissions.write && "write",
                    b.permissions.owner && "owner",
                  ]
                    .filter(Boolean)
                    .join("+")}
                </span>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-muted">Secret key:</span>
              {secret[k.accessKeyId] !== undefined ? (
                <>
                  <code className="break-all rounded border border-line bg-bg2 px-1.5 py-0.5 text-[11.5px]">
                    {secret[k.accessKeyId]}
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      void navigator.clipboard?.writeText(
                        secret[k.accessKeyId] ?? "",
                      )
                    }
                  >
                    Copy
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setSecret((m) => {
                        const n = { ...m };
                        delete n[k.accessKeyId];
                        return n;
                      })
                    }
                  >
                    Hide
                  </Button>
                </>
              ) : (
                <>
                  <span className="font-mono text-faint">••••••••••••</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void reveal(k.accessKeyId)}
                  >
                    Reveal
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function SecretsTab() {
  const { client } = useAuth();
  const [editing, setEditing] = React.useState<string | null>(null);
  const q = useQuery({
    queryKey: ["cluster-secrets"],
    queryFn: () =>
      client.apiFetch<{
        secrets: SecretSpec[] | null;
        missing_cluster_secrets: string[] | null;
        unclassified: string[] | null;
        node: string;
        note?: string;
      }>("/cluster/secrets"),
  });

  const columns: DataListColumn<SecretSpec>[] = [
    {
      key: "name",
      label: "Secret",
      sortable: true,
      render: (s) => <span className="font-mono text-[12px]">{s.name}</span>,
    },
    {
      key: "scope",
      label: "Scope",
      sortable: true,
      // The distinction IS the feature. A cluster secret not copied leaves a node holding
      // ciphertext it cannot read; a per-node secret that IS copied puts back the shared identity
      // the per-node accounts were built to end.
      render: (s) => (
        <StatusPill tone={s.scope === "cluster" ? "accent" : "neutral"}>
          {s.scope}
        </StatusPill>
      ),
    },
    {
      key: "value",
      label: "Value (this node)",
      // Masked until asked for. A reveal is an audited POST (who, when, from where); the value itself
      // is never written to the audit log. Oz 2026-09-25: every secret must be visible to a super_admin.
      render: (s) =>
        s.present === false ? (
          <span className="text-[12px] text-muted">not on this node</span>
        ) : (
          <RevealSecret name={s.name} />
        ),
    },
    {
      key: "edit",
      label: "",
      render: (s) =>
        s.editable ? (
          <Button size="sm" variant="soft" onClick={() => setEditing(s.name)}>
            Change
          </Button>
        ) : (
          <span
            className="text-[11px] text-faint"
            title="no module can move this secret's consumers onto a new value yet"
          >
            view only
          </span>
        ),
    },
    {
      key: "why",
      label: "Why it is scoped that way",
      render: (s) => (
        <span className="text-[12px] leading-relaxed text-muted">{s.why}</span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {(q.data?.missing_cluster_secrets?.length ?? 0) > 0 && (
        <Section title="Missing cluster secrets" bodyClassName="p-0">
          <div className="px-4 py-3 text-[12px] leading-relaxed">
            These are cluster-scoped and absent on this node, so anything
            encrypted with them elsewhere is unreadable here — and nothing
            reports an error, because decryption simply returns noise.
            <div className="mt-2 flex flex-wrap gap-1.5">
              {q.data?.missing_cluster_secrets?.map((n) => (
                <span
                  key={n}
                  className="rounded border border-line bg-bg2 px-1.5 py-0.5 font-mono text-[11px]"
                >
                  {n}
                </span>
              ))}
            </div>
          </div>
        </Section>
      )}

      <Section
        title="Secret scope"
        hint={q.data ? `${q.data.secrets?.length ?? 0} classified` : undefined}
        bodyClassName="p-0"
      >
        <div className="p-4">
          <DataList
            items={q.data?.secrets ?? []}
            columns={columns}
            getRowKey={(s) => s.name}
            loading={q.isLoading}
            error={q.error}
            errorContext="the secret registry"
            searchable
            searchPlaceholder="Search secrets…"
            emptyMessage="No secrets classified."
          />
        </div>
      </Section>

      <GarageKeys />
      {editing && (
        <EditSecret name={editing} onClose={() => setEditing(null)} />
      )}

      {(q.data?.unclassified?.length ?? 0) > 0 && (
        <Section title="Unclassified" bodyClassName="p-0">
          <div className="px-4 py-3 text-[12px] leading-relaxed text-muted">
            Present on this node but not in the registry, so they are treated as
            per-node and never copied. Surfaced rather than swept along — the
            safe default asks a question instead of silently sharing something.
            <div className="mt-2 flex flex-wrap gap-1.5">
              {q.data?.unclassified?.map((n) => (
                <span
                  key={n}
                  className="rounded border border-line bg-bg2 px-1.5 py-0.5 font-mono text-[11px]"
                >
                  {n}
                </span>
              ))}
            </div>
          </div>
        </Section>
      )}

      <Section title="Actions">
        <ActionCard
          title="Pull cluster secrets"
          description="Fetches the cluster-scoped secrets from the node that holds them. Per-node secrets are never touched, and app_key is never adopted over an existing one — replacing it would turn every encrypted column on this node into noise."
          actionLabel="Pull"
          danger="medium"
          confirm={{
            title: "Pull cluster secrets to this node?",
            body: "Cluster-scoped values are overwritten with the authoritative copies. Per-node credentials are left alone.",
          }}
          run={() =>
            client.apiFetch("/cluster/secrets/pull", { method: "POST" })
          }
          invalidates={[["cluster-secrets"]]}
        />
      </Section>
    </div>
  );
}

// ── Updates ───────────────────────────────────────────────────────────────────────────────

function UpdatesTab() {
  const { client } = useAuth();
  // The two facts that decide whether a rollout can work: can this node sign, and is there anyone
  // to send it to. Read here so the action can SHOW them rather than fail on press.
  const master = useQuery({
    queryKey: ["cluster-master"],
    queryFn: () => client.apiFetch<{ i_hold_key?: boolean }>("/cluster/master"),
    refetchInterval: 30000,
  });
  const nodes = useQuery({
    queryKey: ["cluster-nodes"],
    queryFn: () =>
      client.apiFetch<{ items?: { name: string }[] }>("/cluster/nodes"),
    refetchInterval: 30000,
  });
  // Is anything actually staged, and is it signed? Both cards below were offered unconditionally
  // and nothing anywhere reported it — the only way to find out was to press one and read the
  // error.
  const staged = useQuery({
    queryKey: ["cluster-staged"],
    queryFn: () =>
      client.apiFetch<{
        staged: boolean;
        signed: boolean;
        problem?: string;
        note?: string;
        modified?: string;
        sha256?: string;
      }>("/cluster/updates/staged"),
    refetchInterval: 30000,
  });
  return (
    <div className="space-y-4">
      <Section title="Binary updates">
        <div className="px-1 pb-2 text-[12px] leading-relaxed text-muted">
          A staged binary is applied only if it carries a signature this
          cluster's CA verifies, so a file appearing in the staging directory is
          not enough to run it. Signing requires the CA private key and
          therefore happens on one node; the signature is then valid everywhere.
        </div>
        <div className="space-y-3">
          {/* The one-call form, FIRST, because it is what an operator almost always wants, and
                the three-step form below is what they stop halfway through. Rolling an update out
                took stage + sign + rollout as separate requests, and a half-finished sequence
                leaves members on the old binary while the master reports itself updated. */}
          <ActionCard
            title="Roll this build out to every other node"
            description={
              <>
                Makes every member node run the binary this node is running now
                — signed with the cluster CA and pushed one node at a time over
                the signed peer channel. Members are never updated any other
                way.
              </>
            }
            danger="high"
            actionLabel="Roll out"
            // PREFLIGHT, not a veto: the two facts that decide whether this can work at all, put
            // in front of the operator rather than used to disable the button. Without the CA key
            // the call refuses server-side before writing anything, so pressing it is safe — and
            // seeing WHY beats a greyed-out control that explains nothing.
            preflight={[
              {
                ok: !!master.data?.i_hold_key,
                label: master.data?.i_hold_key
                  ? "This node holds the cluster CA key, so it can sign the release"
                  : "This node does NOT hold the cluster CA key",
                detail: master.data?.i_hold_key
                  ? undefined
                  : "only the key holder can sign a binary the members will accept; the call will refuse",
              },
              {
                ok: (nodes.data?.items?.length ?? 0) > 1,
                label:
                  (nodes.data?.items?.length ?? 0) > 1
                    ? `${(nodes.data?.items?.length ?? 1) - 1} member node(s) will be updated`
                    : "There are no other nodes in this cluster",
                detail:
                  (nodes.data?.items?.length ?? 0) > 1
                    ? "this node is updated last, with self-update"
                    : "nothing to roll out to",
              },
            ]}
            confirm={{
              title: "Roll this build out to the cluster?",
              body: (
                <>
                  Each member is restarted in turn. A member that fails is
                  reported by name and the rest are still attempted — a cluster
                  half-updated by a stall is worse than one where the failure is
                  named.
                </>
              ),
              confirmLabel: "Roll out",
            }}
            run={async () => {
              const r: any = await client.apiFetch("/cluster/updates/publish", {
                method: "POST",
              });
              const d = r?.data ?? r;
              // Report PER NODE. The call can answer ok overall while one member did not take it,
              // and a member left on the old binary is the exact split this is meant to close.
              const lines = (d?.results ?? []).map(
                (x: any) =>
                  (x.ok ? "\u2713 " : "\u2717 ") +
                  x.node +
                  (x.error ? " \u2014 " + x.error : ""),
              );
              return {
                ...d,
                summary: [d?.note, ...lines].filter(Boolean).join("\n"),
              };
            }}
            invalidates={[["cluster-nodes"], ["cluster-staged"]]}
          />
          <ActionCard
            title="Sign the staged binary"
            unavailable={
              staged.data && !staged.data.staged
                ? "Nothing is staged on this node, so there is no binary to sign. Staging is what scripts/deploy-node.sh does before it asks for a signature."
                : undefined
            }
            description="Signs whatever has been staged, using this node's copy of the CA private key. Only the key holder can do this — on any other node it fails, which is the intended answer rather than a fault."
            actionLabel="Sign"
            danger="medium"
            confirm={{
              title: "Sign the staged binary?",
              body: "The signature attests that this build may run anywhere in the cluster. Sign only what you staged.",
            }}
            run={() =>
              client.apiFetch("/cluster/updates/sign", { method: "POST" })
            }
          />
          <ActionCard
            title="Apply the staged binary"
            unavailable={
              staged.data && !staged.data.staged
                ? "Nothing is staged on this node, so there is nothing to apply."
                : staged.data && !staged.data.signed
                  ? staged.data.problem
                    ? `The staged binary does not verify against its signature: ${staged.data.problem}`
                    : "The staged binary is not signed. A node only applies a binary whose signature this cluster's CA verifies."
                  : undefined
            }
            description="Verifies the signature and replaces the running binary, restarting the panel. Refuses an unsigned or badly signed file rather than trusting the filesystem."
            actionLabel="Apply"
            danger="high"
            confirm={{
              title: "Apply the staged update on this node?",
              body: "The panel restarts. Host services keep running throughout — mail, web and the databases are unaffected — but this console will be briefly unavailable.",
              confirmLabel: "Apply update",
            }}
            run={() =>
              client.apiFetch("/cluster/self-update", { method: "POST" })
            }
          />
        </div>
      </Section>
    </div>
  );
}

// ── Recovery ──────────────────────────────────────────────────────────────────────────────

// InterventionsPanel — what needs a HUMAN right now, and why.
//
// First on the tab, before any action, because it is the question an operator actually has during
// an incident. Everything else in the failover path exists to avoid needing a person; this says
// when that has run out.
//
// Every entry is a state the drills reached while the platform reported itself healthy — Redis
// read-only while the site answered 200, two arbitrators running at once, a failover that left no
// successor so the next one could not happen. Silence here means every check RAN and found nothing;
// a check that could not gather evidence is listed separately rather than counted as a pass.
function InterventionsPanel() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["cluster-interventions"],
    queryFn: () =>
      client.apiFetch<{
        status?: string;
        items?: {
          severity: string;
          what: string;
          why: string;
          do: string;
          evidence?: string;
        }[];
        unmeasured?: string[];
        note?: string;
      }>("/cluster/interventions"),
    refetchInterval: 30000,
  });
  const d: any = (q.data as any)?.data ?? q.data;
  const items = (d?.items ?? []) as any[];
  const unmeasured = (d?.unmeasured ?? []) as string[];
  const urgent = items.filter((i) => i.severity === "urgent").length;

  const tone = (sev: string) =>
    sev === "urgent"
      ? "border-danger/40 bg-danger/5"
      : sev === "soon"
        ? "border-warn/40 bg-warn/5"
        : "border-line";

  return (
    <Section title="Does anything need a person?">
      <StateView
        loading={q.isLoading}
        error={q.error}
        errorContext="the intervention check"
      >
        <div className="px-1 pb-2 text-[12px] leading-relaxed text-muted">
          Measured on <em>this node</em>, now — each check asks the service
          rather than reading our own records.{" "}
          {items.length === 0 && unmeasured.length === 0 ? (
            <strong className="text-ok">
              Every check ran and found nothing.
            </strong>
          ) : (
            <strong className={urgent > 0 ? "text-danger" : "text-warn"}>
              {d?.status}
            </strong>
          )}
        </div>
        <div className="space-y-2 px-1 pb-2">
          {items.map((i, n) => (
            <div key={n} className={`rounded border p-2 ${tone(i.severity)}`}>
              <div className="text-[13px] font-medium">
                <span className="uppercase opacity-70">[{i.severity}]</span>{" "}
                {i.what}
              </div>
              <div className="mt-1 text-[12px] leading-relaxed text-muted">
                {i.why}
              </div>
              <div className="mt-1 text-[12px] leading-relaxed">
                <strong>Do:</strong> {i.do}
              </div>
              {i.evidence && (
                <div className="mt-1 font-mono text-[11px] text-muted">
                  {i.evidence}
                </div>
              )}
            </div>
          ))}
          {unmeasured.map((u, n) => (
            <div
              key={`u${n}`}
              className="rounded border border-line p-2 text-[12px] text-muted"
            >
              <strong>Could not be checked:</strong> {u} — this is not the same
              as nothing being wrong.
            </div>
          ))}
        </div>
      </StateView>
    </Section>
  );
}

// InternetProbesPanel — the targets this node dials to prove its OWN internet access.
//
// These decide whether a node can tell "my peers are gone" from "my own link is down". The boot
// gate needs both facts because they demand opposite actions: a node whose link is broken must
// never conclude the cluster is gone and promote itself. With none configured the node reports
// connectivity as UNMEASURABLE, which is treated as not-up and never as working.
//
// Per node on purpose, and written to that node's config file: what a node can reach is a fact
// about where the node sits, not about the cluster.
function InternetProbesPanel() {
  const { client } = useAuth();
  const qc = useQueryClient();
  type Probe = { host: string; port?: number; proto?: string; note?: string };
  const q = useQuery({
    queryKey: ["cluster-internet-probes"],
    queryFn: () =>
      client.apiFetch<{
        probes?: Probe[];
        source?: string;
        state?: string;
        detail?: string;
        note?: string;
      }>("/cluster/internet-probes"),
  });
  const d: any = (q.data as any)?.data ?? q.data;
  const [rows, setRows] = React.useState<Probe[] | null>(null);
  const list: Probe[] = rows ?? (d?.probes as Probe[]) ?? [];
  const [tested, setTested] = React.useState<
    { target: string; reached: boolean }[] | null
  >(null);

  const save = useMutation({
    mutationFn: (probes: Probe[]) =>
      client.apiFetch<unknown>("/cluster/internet-probes", {
        method: "PUT",
        body: JSON.stringify({ probes }),
      }),
    onSuccess: () => {
      setRows(null);
      setTested(null);
      qc.invalidateQueries({ queryKey: ["cluster-internet-probes"] });
    },
  });
  const test = useMutation({
    mutationFn: (probes: Probe[]) =>
      client.apiFetch<{ results?: { target: string; reached: boolean }[] }>(
        "/cluster/internet-probes/test",
        { method: "POST", body: JSON.stringify({ probes }) },
      ),
    onSuccess: (r: any) => setTested((r?.data ?? r)?.results ?? []),
  });

  const set = (i: number, patch: Partial<Probe>) => {
    const next = list.map((p, n) => (n === i ? { ...p, ...patch } : p));
    setRows(next);
  };

  const state = d?.state as string | undefined;
  return (
    <Section title="Internet connectivity targets">
      <div className="px-1 pb-2 text-[12px] leading-relaxed text-muted">
        Dialled only to establish whether a node has connectivity — never to
        reach the cluster. These apply to the <strong>whole cluster</strong> and
        are mirrored onto every node, so a node that joins inherits them: a
        joiner with none reports connectivity as unmeasurable, which is treated
        as not-up, so it would refuse to take over even when it should.
        {d?.source ? (
          <>
            {" "}
            Currently <strong>{d.source}</strong>.
          </>
        ) : null}{" "}
        A node that cannot reach its peers needs to know whether the fault is
        its own before it decides anything: a broken link and a dead cluster
        look identical from here, and they call for opposite actions.{" "}
        <strong>Never point one at a cluster peer</strong> — a peer cannot
        answer that question, and the server refuses it.
        {state && (
          <div className="mt-1">
            Currently{" "}
            <strong>
              {state === "up"
                ? "reachable"
                : state === "down"
                  ? "NOT reachable"
                  : "unmeasurable"}
            </strong>
            {d?.detail ? <> — {d.detail}</> : null}
            {state === "unmeasurable" && (
              <>
                {" "}
                . With nothing configured this node stays in the most cautious
                branch on every boot.
              </>
            )}
          </div>
        )}
      </div>
      <div className="space-y-2 px-1">
        {list.map((p, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <input
              className="w-56 rounded border border-line bg-surface px-2 py-1 text-[13px]"
              placeholder="host or IP outside the cluster"
              value={p.host ?? ""}
              onChange={(e) => set(i, { host: e.target.value })}
            />
            <select
              className="rounded border border-line bg-surface px-2 py-1 text-[13px]"
              value={p.proto ?? "tcp"}
              onChange={(e) => set(i, { proto: e.target.value })}
            >
              <option value="tcp">tcp</option>
              <option value="icmp">icmp</option>
            </select>
            {(p.proto ?? "tcp") === "tcp" ? (
              <input
                className="w-24 rounded border border-line bg-surface px-2 py-1 text-[13px]"
                placeholder="port"
                inputMode="numeric"
                value={p.port ?? 443}
                onChange={(e) => set(i, { port: Number(e.target.value) || 0 })}
              />
            ) : (
              // ICMP has no ports. Showing a disabled box rather than an editable one that is
              // ignored: a setting displayed without what governs it is the interlock fault.
              <span className="w-24 px-2 py-1 text-[12px] text-muted">
                no port
              </span>
            )}
            <input
              className="w-48 rounded border border-line bg-surface px-2 py-1 text-[13px]"
              placeholder="note (why this target)"
              value={p.note ?? ""}
              onChange={(e) => set(i, { note: e.target.value })}
            />
            <button
              className="rounded border border-line px-2 py-1 text-[12px]"
              onClick={() => setRows(list.filter((_, n) => n !== i))}
            >
              remove
            </button>
            {tested?.[i] && (
              <span
                className={
                  tested[i].reached
                    ? "text-[12px] text-ok"
                    : "text-[12px] text-danger"
                }
              >
                {tested[i].reached ? "reached" : "no answer"} (
                {tested[i].target})
              </span>
            )}
          </div>
        ))}
        <div className="flex flex-wrap gap-2 pt-1">
          <button
            className="rounded border border-line px-2 py-1 text-[12px]"
            onClick={() =>
              setRows([...list, { host: "", proto: "tcp", port: 443 }])
            }
          >
            add target
          </button>
          <button
            className="rounded border border-line px-2 py-1 text-[12px]"
            disabled={test.isPending || list.length === 0}
            onClick={() => test.mutate(list)}
          >
            {test.isPending ? "testing…" : "test without saving"}
          </button>
          <button
            className="rounded border border-line px-2 py-1 text-[12px]"
            disabled={save.isPending}
            onClick={() => save.mutate(list)}
          >
            {save.isPending ? "saving…" : "save to this node"}
          </button>
        </div>
        {(save.error || test.error) && (
          <div className="pt-1 text-[12px] text-danger">
            {String(
              (save.error as any)?.message ?? (test.error as any)?.message,
            )}
          </div>
        )}
        <div className="pb-1 text-[12px] text-muted">
          Use more than one, and prefer targets that fail independently. A
          single endpoint is a single point of wrongness: when it blips, this
          node concludes the internet is down.
        </div>
      </div>
    </Section>
  );
}

// RecoveryTokenPanel — the credential for the case where the panel cannot log you in.
//
// It authenticates a super_admin for the recovery routes while Galera has no quorum and DB-backed
// auth cannot answer. Until now it was reachable only by having root SSH and knowing that
// `ozpanel admin recovery-token` exists, which is the wrong shape for a credential whose purpose is
// the moment you are locked out. Measured 2026-09-04: the panel was unusable for an hour because
// the database was fenced, this token was the answer, and nobody reached for it.
//
// Shown to an authenticated super_admin, who already holds strictly more power than it grants — so
// revealing it escalates nothing, while hiding it leaves a credential retrievable only in
// conditions where retrieval is impossible.
function RecoveryTokenPanel() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const [shown, setShown] = React.useState(false);
  const rt = useQuery({
    queryKey: ["cluster-recovery-token"],
    queryFn: () =>
      client.apiFetch<{
        present?: boolean;
        token?: string;
        path?: string;
        rotated_at?: string;
        node?: string;
        scope?: string;
        note?: string;
        warning?: string;
        commands?: Record<string, string>;
      }>("/cluster/recovery-token"),
  });
  const rotate = useMutation({
    mutationFn: () =>
      client.apiFetch<unknown>("/cluster/recovery-token/rotate", {
        method: "POST",
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["cluster-recovery-token"] }),
  });
  const d = rt.data as any;
  const data = d?.data ?? d;
  return (
    <Section title="Recovery token">
      <div className="px-1 pb-2 text-[12px] leading-relaxed text-muted">
        This authenticates you to <em>this node</em> when its database cannot —
        the case where normal login fails. Each node has its own.{" "}
        <strong>Copy it somewhere outside the cluster</strong>: storing it only
        here defeats the point, because you cannot open this page during the
        incident it exists for.
      </div>
      {data?.present === false && (
        <div className="rounded border border-bad px-3 py-2 text-[12.5px] leading-relaxed text-bad">
          {data?.warning ??
            "This node has no recovery token — there is no emergency access to it."}
        </div>
      )}
      {data?.present && (
        <div className="space-y-2">
          <div className="text-[12px] text-muted">
            {data.node} · last rotated {data.rotated_at ?? "unknown"} ·{" "}
            {data.path}
          </div>
          <div className="flex items-center gap-2">
            <button className="btn btn-sm" onClick={() => setShown((v) => !v)}>
              {shown ? "Hide" : "Reveal token and commands"}
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={() => rotate.mutate()}
            >
              Rotate (revokes the current one)
            </button>
          </div>
          {shown && (
            <>
              <div className="break-all rounded border border-line bg-bg2 p-2 font-mono text-[12px]">
                {data.token}
              </div>
              {/* Rendered ready to paste, not described. An operator mid-incident should copy a
                  line, not compose one from documentation they also cannot reach. */}
              {Object.entries(data.commands ?? {}).map(([k, v]) => (
                <pre
                  key={k}
                  className="overflow-x-auto whitespace-pre-wrap break-all rounded border border-line bg-bg2 p-2 font-mono text-[11.5px]"
                >
                  {String(v)}
                </pre>
              ))}
              <div className="text-[11.5px] leading-relaxed text-muted">
                {data.note}
              </div>
            </>
          )}
        </div>
      )}
    </Section>
  );
}

// ── Recovery grants ───────────────────────────────────────────────────────────────────────
//
// A grant lets someone WITHOUT a broken node's recovery token recover it: signed here with the
// cluster CA key, checked there against the CA certificate on that node's own disk, no database
// needed. It names its node(s), what it allows and when it ends. Skipping the peer check (force)
// is reachable ONLY through a grant that says so.
function IssueRecoveryGrant() {
  const { client } = useAuth();
  const { notify } = useToast();
  const nodes = useQuery({
    queryKey: ["cluster-nodes"],
    queryFn: () => client.apiFetch<{ items?: { name: string }[] }>("/cluster/nodes"),
    refetchInterval: 30000,
  });
  const [picked, setPicked] = React.useState<string[]>([]);
  const [allow, setAllow] = React.useState<"diagnose" | "recover" | "force">("recover");
  const [ttl, setTtl] = React.useState(60);
  const [reason, setReason] = React.useState("");
  type Issued = { grant: string; id: string; expires_at: string; nodes: string[]; allow: string };
  const [issued, setIssued] = React.useState<Issued | null>(null);
  const [busy, setBusy] = React.useState(false);

  async function issue() {
    setBusy(true);
    try {
      const r = await client.apiFetch<Issued>("/recovery/grants", {
        method: "POST",
        body: JSON.stringify({ nodes: picked, allow, ttl_minutes: ttl, reason }),
      });
      setIssued(r);
    } catch (e) {
      notify({ kind: "error", title: "No grant issued", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="Recovery grants">
      <div className="space-y-3 p-4 text-[13px]">
        <p className="leading-relaxed text-muted">
          Lets someone without a node's recovery token recover that node in recovery mode (
          <span className="font-mono">/admin/recovery</span> on it). Signed with this node's cluster CA key, so it is
          issued on the primary or a successor; the broken node checks it against its own copy of the CA certificate,
          with no database. Every use is recorded on that node with this grant's id and your name.
        </p>
        <div className="flex flex-wrap gap-3">
          {(nodes.data?.items ?? []).map((n) => (
            <label key={n.name} className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={picked.includes(n.name)}
                onChange={(e) =>
                  setPicked((p) => (e.target.checked ? [...p, n.name] : p.filter((x) => x !== n.name)))
                }
              />
              {n.name}
            </label>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1.5">
            Allows
            <select
              value={allow}
              onChange={(e) => setAllow(e.target.value as typeof allow)}
              className="rounded border border-line2 bg-bg px-2 py-1"
            >
              <option value="diagnose">diagnose — read and diagnose only</option>
              <option value="recover">recover</option>
              <option value="force">force — may skip the peer-silence check</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5">
            Valid for
            <input
              type="number"
              min={1}
              max={1440}
              value={ttl}
              onChange={(e) => setTtl(Number(e.target.value) || 60)}
              className="w-20 rounded border border-line2 bg-bg px-2 py-1"
            />
            minutes
          </label>
          <input
            placeholder="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="min-w-[200px] flex-1 rounded border border-line2 bg-bg px-2 py-1"
          />
        </div>
        {allow === "force" && (
          <p className="rounded border border-line bg-critsoft px-3 py-2 text-[12.5px] text-crit">
            A force grant can promote a node while a peer is still serving — two writable clusters. Issue it only
            when the peer is known destroyed and will never return.
          </p>
        )}
        <Button
          variant={allow === "force" ? "danger" : "accent"}
          disabled={busy || picked.length === 0}
          onClick={() => void issue()}
        >
          Issue grant
        </Button>
        {issued && (
          <div className="rounded border border-line bg-bg p-3">
            <p className="mb-1.5 text-[12.5px] text-muted">
              Grant {issued.id} — {issued.allow} on {issued.nodes.join(", ")}, expires{" "}
              {formatRelative(issued.expires_at)}. Shown once:
            </p>
            <textarea
              readOnly
              value={issued.grant}
              rows={4}
              className="w-full rounded border border-line2 bg-surface p-2 font-mono text-[11.5px] break-all"
              onFocus={(e) => e.currentTarget.select()}
            />
          </div>
        )}
      </div>
    </Section>
  );
}

function RecoveryTab() {
  const { client } = useAuth();
  // Is taking over warranted RIGHT NOW? The server probes every peer on the ports that mean a
  // database, a filesystem or an operator could reach it, and reports how long they have all been
  // silent. Offered unconditionally before this: a four-second outage on this cluster today was
  // enough for repmgrd to promote a standby beside a live primary, and a person pressing an
  // enabled button reasonably assumes something checked.
  const survive = useQuery({
    queryKey: ["cluster-survive-status"],
    queryFn: () =>
      client.apiFetch<{
        can_take_over?: boolean;
        reason?: string;
        peer_silent?: boolean;
      }>("/cluster/survive-status"),
    refetchInterval: 20000,
  });

  // The arbitrator, from its OWNER. This read GET /cluster/garbd, removed in 01146e20 with the toggle
  // it served — a 404 rendered as an error box. Where it belongs is derived (GET /cluster/arbiter);
  // whether it runs here is the garbd module's measurement, where null means NOT MEASURED.
  const garbd = useQuery({
    queryKey: ["garbd-status"],
    queryFn: () =>
      client.apiFetch<{
        running: boolean | null;
        node: string;
        cluster_size: number | null;
        garbd_contributing: boolean | null;
        unread?: Record<string, string> | null;
      }>("/galera/garbd/status"),
    refetchInterval: 30000,
  });
  const arbiter = useQuery({
    queryKey: ["cluster-arbiter"],
    queryFn: () =>
      client.apiFetch<{ arbiter_home: string; declared_primary: string; this_node: string; belongs_here: boolean; how?: string }>(
        "/cluster/arbiter",
      ),
    refetchInterval: 60000,
  });
  const unreadText = (k: string) => garbd.data?.unread?.[k];

  const [renameFqdn, setRenameFqdn] = React.useState("");
  const [renameNode, setRenameNode] = React.useState("");
  const [renamePlan, setRenamePlan] = React.useState<{ key: string; plan: unknown } | null>(null);
  const renameKey = `${renameFqdn.trim()}|${renameNode.trim()}`;

  return (
    <div className="space-y-4">
      {/* First on the tab: the credential you need to reach everything below it when the panel
          cannot log you in. Placed before the actions rather than after, because it is the
          prerequisite, not an appendix. */}
      <InterventionsPanel />
      <RecoveryTokenPanel />
      <IssueRecoveryGrant />
      <InternetProbesPanel />
      <Section
        title="Galera arbitrator"
        hint={arbiter.data ? (arbiter.data.arbiter_home ? `belongs on ${arbiter.data.arbiter_home}` : "no live successor to host it") : undefined}
        bodyClassName="p-0"
      >
        <StateView loading={garbd.isLoading} error={garbd.error} errorContext="the arbitrator on this node">
          <KeyValue
            rows={[
              {
                key: "home",
                label: "Its home",
                value: arbiter.error ? "unread" : arbiter.data?.arbiter_home || "none",
                hint: "Derived from the signed succession order and liveness — the highest-ranked live successor — never recorded. The galera module starts it there on its role pass.",
              },
              {
                key: "belongs_here",
                label: "Belongs on this node",
                value: arbiter.data?.belongs_here,
              },
              {
                key: "running",
                label: `Running on ${garbd.data?.node ?? "this node"}`,
                value: garbd.data?.running == null ? `not measured: ${unreadText("running") ?? "no answer"}` : garbd.data.running,
                goodWhen: arbiter.data?.belongs_here ? true : undefined,
              },
              {
                key: "cluster_size",
                label: "Galera members (it counts the arbitrator)",
                value: garbd.data?.cluster_size == null ? `not measured: ${unreadText("cluster_size") ?? "no answer"}` : garbd.data.cluster_size,
              },
              {
                key: "contributing",
                label: "Contributing a vote",
                value: garbd.data?.garbd_contributing == null ? "not measured" : garbd.data.garbd_contributing,
                hint: "Running AND in a cluster of three or more members — a running arbitrator that is not a member votes for nothing.",
              },
            ]}
          />
        </StateView>
        {/* By hand, on THIS node. The galera module converges the arbitrator to its derived home on
            its next role pass either way, so these are the immediate form of what it will do — or, on
            the wrong node, a change it will undo. Each card says which, before the click. */}
        <div className="space-y-3 border-t border-line p-4">
          <ActionCard
            title={`Start the arbitrator on ${garbd.data?.node ?? "this node"}`}
            description="Starts garbd here now, first filling any arbitrator setting the stock file leaves blank (peers, group). It adds the arbitrator's vote to Galera's quorum; on a node that is not its home the galera module stops it again on its next role pass."
            actionLabel="Start garbd"
            danger="medium"
            unavailable={garbd.data?.running === true ? "The arbitrator is already running on this node." : undefined}
            preflight={[
              {
                ok: !!arbiter.data?.belongs_here,
                label: arbiter.data?.belongs_here ? "This node is the arbitrator's home" : `This node is not its home (${arbiter.data?.arbiter_home || "none"})`,
                detail: "Two arbitrators make the vote total even, so a partition can stop both halves.",
              },
            ]}
            confirm={{
              title: "Start garbd on this node?",
              body: arbiter.data?.belongs_here
                ? "It joins the Galera cluster as a voting member within seconds."
                : `Its home is ${arbiter.data?.arbiter_home || "no node"}. A second running arbitrator makes the vote total even until the galera module stops this one.`,
              confirmLabel: "Start",
            }}
            run={() => client.apiFetch("/galera/garbd/start", { method: "POST" })}
            invalidates={[["garbd-status"]]}
          />
          <ActionCard
            title={`Stop the arbitrator on ${garbd.data?.node ?? "this node"}`}
            description="Stops garbd here now. The cluster loses the arbitrator's vote until it runs again: with it gone, losing one data node can leave the rest without quorum."
            actionLabel="Stop garbd"
            danger="high"
            unavailable={garbd.data?.running === false ? "The arbitrator is not running on this node." : undefined}
            preflight={[
              {
                ok: !arbiter.data?.belongs_here,
                label: arbiter.data?.belongs_here ? "This node IS its home — the galera module restarts it on its next pass" : "This node is not its home, so stopping it here is what the galera module would do",
              },
            ]}
            confirm={{
              title: "Stop garbd on this node?",
              body: "Until an arbitrator runs again, Galera counts only the data nodes. If another node fails in that window the survivors may not hold a majority and stop accepting writes.",
              confirmLabel: "Stop",
            }}
            run={() => client.apiFetch("/galera/garbd/stop", { method: "POST" })}
            invalidates={[["garbd-status"]]}
          />
        </div>
      </Section>

      <Section title="Actions">
        <div className="space-y-3">
          {/* The PLANNED handover, above the emergency one. Promotion assumes the peer is gone
                and rewinds whatever disagrees; a switchover coordinates with a LIVE peer and
                discards nothing. Offering them in that order is how an operator picks the right
                one — tonight the wrong choice would have destroyed every write made while the
                other node was serving alone. */}
          {/* The PLANNED handover is the declaration's: it moves PostgreSQL, Redis, the arbitrator and
              the seat together, through the one chain that decides who is primary. A card here called
              /repmgrd/switchover, which moves PostgreSQL ALONE — a second actor on the role, which the
              declaration reconciler then pulls back. */}
          <div className="rounded-lg border border-line bg-surface p-4 text-[12.5px] leading-relaxed">
            <div className="text-[13px] font-medium">Hand the workload to a healthy node</div>
            <p className="mt-1 text-muted">
              The planned handover — both nodes alive, nothing discarded — is under Nodes → Cluster state: each live
              node has a handover button there (POST /cluster/handover-to), which moves PostgreSQL, Redis and the
              arbitrator together with the declaration. Use the survivor promotion below only when the other nodes are gone.
            </p>
          </div>
          <ActionCard
            title="Promote this node as the survivor"
            // PREFLIGHT, not a block. The probe that reports a peer as answering runs on a node
            // that may itself be the partitioned half; if it is wrong, disabling the control
            // removes the only recovery action at the only moment it matters. The operator knows
            // whether the peer is really dead — this node only knows whether it can reach it.
            preflight={
              survive.data
                ? [
                    {
                      ok: !!survive.data.can_take_over,
                      label: survive.data.peer_silent
                        ? "No peer is answering"
                        : "A peer is answering",
                      detail: survive.data.reason,
                    },
                  ]
                : [
                    {
                      ok: false,
                      label: "Peer reachability could not be measured",
                      detail: "proceed only if you know the peer is gone",
                    },
                  ]
            }
            description="Bootstraps the database cluster from this node after the others are gone. Use only when this node is the most advanced survivor — a bootstrap from a node that is behind discards the writes the other one had."
            actionLabel="Promote"
            danger="high"
            confirm={{
              title: "Promote this node as the sole survivor?",
              body: "This node becomes the source of truth and forms a new primary component. Check that its committed position is at least as advanced as any node that might return, because their divergent writes will be discarded when they rejoin.",
              confirmLabel: "Promote survivor",
            }}
            run={() =>
              client.apiFetch("/cluster/promote-survivor", { method: "POST" })
            }
            invalidates={[["cluster-nodes"], ["cluster-convergence"]]}
          />
          <div className="space-y-2 rounded-lg border border-line p-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label="New FQDN" value={renameFqdn} onChange={setRenameFqdn} mono placeholder="node12.example.com" />
              <Field label="New node name (optional)" value={renameNode} onChange={setRenameNode} mono placeholder="the FQDN's first label" />
            </div>
            <ActionCard
              title="Preview the rename"
              description="Asks the server what the rename would do (dry run) — the steps it re-runs and what it refuses — and changes nothing."
              actionLabel="Preview"
              unavailable={!renameFqdn.trim().includes(".") ? "Give the fully qualified name first." : undefined}
              run={async () => {
                const r = await client.apiFetch("/cluster/rename", {
                  method: "POST",
                  body: JSON.stringify({ fqdn: renameFqdn.trim(), node: renameNode.trim() || undefined, dry_run: true }),
                });
                setRenamePlan({ key: renameKey, plan: r });
                return r;
              }}
            />
            <ActionCard
              title="Rename this node"
              description="Rewrites this node's config, re-runs every step that embeds its name (hostname, Postfix HELO, the panel's own vhost) and renames its registry row in place — its identity (uuid) does not change. Refused while a storage brick here holds data."
              actionLabel="Rename"
              danger="high"
              unavailable={
                !renamePlan || renamePlan.key !== renameKey
                  ? "Preview this exact rename first — the plan above is what the rename will do."
                  : undefined
              }
              confirm={{
                title: `Rename this node to ${renameFqdn.trim()}?`,
                body: "Every record keyed on the current name is rewritten. Anything holding the old name outside the cluster — its DNS A record, a monitoring target — does not change, and the node's panel answers on the new name only once that name resolves to it.",
                confirmLabel: "Rename",
              }}
              run={() =>
                client.apiFetch("/cluster/rename", {
                  method: "POST",
                  body: JSON.stringify({ fqdn: renameFqdn.trim(), node: renameNode.trim() || undefined, dry_run: false }),
                })
              }
              invalidates={[["cluster-nodes"], ["cluster-identity"]]}
            />
          </div>
        </div>
      </Section>
    </div>
  );
}

// ── History: what changed, and who was removed ────────────────────────────────────────────

interface Change {
  gen: number;
  kind: string;
  scope: string;
  op: string;
  origin_node: string;
  made_at: string;
}
/** GET /cluster/evictions. The console declared node_name/node_uuid while the server has always sent
 *  node/uuid, so every row rendered an empty name and the list keyed on undefined. */
interface Eviction {
  node: string;
  uuid: string;
  reason: string;
  evicted_by: string;
  evicted_at: string;
  pending?: string[] | null;
  /** The eviction's own proof, recorded on the tombstone after the run. */
  complete?: boolean;
  /** false = the eviction stopped before it could record any outcome. */
  reported?: boolean;
}

function HistoryTab() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const [act, setAct] = React.useState<{ kind: "readmit" | "finish"; e: Eviction } | null>(null);
  const [actBusy, setActBusy] = React.useState(false);
  const [actResult, setActResult] = React.useState<{ title: string; ok: boolean; body: unknown } | null>(null);
  const [ackLoss, setAckLoss] = React.useState(false);
  const [openEviction, setOpenEviction] = React.useState<string | null>(null);
  const master = useQuery({
    queryKey: ["cluster-master"],
    queryFn: () => client.apiFetch<MasterState>("/cluster/master"),
  });
  const isMaster = master.data?.am_i_leader === true && master.data?.verified === true;
  async function runAct() {
    if (!act) return;
    const { kind, e } = act;
    setActBusy(true);
    try {
      const body =
        kind === "readmit"
          ? await client.apiFetch(`/cluster/nodes/${encodeURIComponent(e.node)}/evict`, { method: "DELETE" })
          : await client.apiFetch(`/cluster/nodes/${encodeURIComponent(e.node)}/evict`, {
              method: "POST",
              body: JSON.stringify({ reason: e.reason, acknowledge_data_loss: ackLoss }),
            });
      setActResult({ title: kind === "readmit" ? `${e.node} readmitted` : `Removal of ${e.node} re-run`, ok: true, body });
    } catch (err) {
      setActResult({ title: kind === "readmit" ? `Readmitting ${e.node} refused` : `Re-running the removal of ${e.node} refused`, ok: false, body: err instanceof Error ? err.message : String(err) });
    } finally {
      setActBusy(false);
      setAct(null);
      void qc.invalidateQueries({ queryKey: ["cluster-evictions"] });
      void qc.invalidateQueries({ queryKey: ["cluster-nodes"] });
    }
  }
  const changes = useQuery({
    queryKey: ["cluster-changes"],
    queryFn: () =>
      client.apiFetch<{ changes: Change[] | null; generation: number }>(
        "/cluster/changes",
      ),
    refetchInterval: 30000,
  });
  const evictions = useQuery({
    queryKey: ["cluster-evictions"],
    queryFn: () =>
      client.apiFetch<{ items: Eviction[] | null; total: number }>(
        "/cluster/evictions",
      ),
  });

  const changeCols: DataListColumn<Change>[] = [
    {
      key: "gen",
      label: "Gen",
      sortable: true,
      render: (c) => <span className="font-mono text-[12px]">{c.gen}</span>,
    },
    { key: "kind", label: "Kind", sortable: true },
    {
      key: "op",
      label: "Op",
      sortable: true,
      render: (c) => (
        <StatusPill tone={c.op === "delete" ? "warn" : "neutral"}>
          {c.op}
        </StatusPill>
      ),
    },
    {
      key: "scope",
      label: "Scope",
      sortable: true,
      render: (c) => <span className="font-mono text-[12px]">{c.scope}</span>,
    },
    { key: "origin_node", label: "From", sortable: true },
    {
      key: "made_at",
      label: "When",
      sortable: true,
      render: (c) => formatRelative(c.made_at),
    },
  ];

  const evictCols: DataListColumn<Eviction>[] = [
    { key: "node", label: "Node", sortable: true, render: (e) => <span className="font-mono">{e.node}</span> },
    {
      key: "complete",
      label: "Outcome",
      value: (e) => (e.complete ? "complete" : e.reported ? "incomplete" : "unrecorded"),
      // The eviction's own proof. Incomplete is a removal that left something behind; unrecorded is
      // one that stopped before it could say — neither is "removed".
      render: (e) => (
        <StatusPill tone={e.complete ? "ok" : "warn"}>
          {e.complete ? "complete" : e.reported ? "incomplete" : "no outcome recorded"}
        </StatusPill>
      ),
    },
    {
      key: "reason",
      label: "Reason",
      render: (e) => e.reason || <span className="text-muted">none given</span>,
    },
    { key: "evicted_by", label: "By", sortable: true },
    {
      key: "evicted_at",
      label: "When",
      sortable: true,
      render: (e) => (e.evicted_at ? formatRelative(e.evicted_at) : "—"),
    },
  ];

  return (
    <div className="space-y-4">
      <Section
        title="Recent cluster changes"
        hint={
          changes.data ? `generation ${changes.data.generation}` : undefined
        }
        bodyClassName="p-0"
      >
        <div className="p-4">
          <DataList
            items={changes.data?.changes ?? []}
            columns={changeCols}
            getRowKey={(c) => `${c.gen}`}
            loading={changes.isLoading}
            error={changes.error}
            errorContext="the change journal"
            searchable
            searchPlaceholder="Search changes…"
            emptyMessage="No changes recorded."
          />
        </div>
      </Section>

      <Section
        title="Evictions"
        hint={evictions.data ? `${evictions.data.total} recorded` : undefined}
        bodyClassName="p-0"
      >
        <div className="p-4">
          <DataList
            items={evictions.data?.items ?? []}
            columns={evictCols}
            getRowKey={(e) => e.uuid || e.node}
            rowActions={(e: Eviction) => (
              <div className="flex justify-end gap-2">
                {!e.complete && isMaster && (
                  <Button size="sm" variant="ghost" onClick={() => { setAckLoss(false); setAct({ kind: "finish", e }); }}>
                    Finish the removal
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setAct({ kind: "readmit", e })}>
                  Readmit
                </Button>
              </div>
            )}
            onRowClick={(e: Eviction) => setOpenEviction(openEviction === (e.uuid || e.node) ? null : e.uuid || e.node)}
            expandedKey={openEviction}
            renderExpanded={(e: Eviction) =>
              e.pending && e.pending.length > 0 ? (
                <ul className="list-disc pl-5 text-[12px] leading-relaxed text-warn">
                  {e.pending.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              ) : (
                <span className="text-[12px] text-muted">The eviction recorded nothing left open.</span>
              )
            }
            loading={evictions.isLoading}
            error={evictions.error}
            errorContext="the eviction record"
            emptyMessage="No node has been evicted."
            emptyHint="An eviction is tombstoned here so the removal survives that node's next reboot — without the record, a node re-registers itself and quietly regains every gate derived from the registry."
          />
          {actResult && (
            <div className={`mt-3 rounded border border-line px-3 py-2 text-[12.5px] ${actResult.ok ? "" : "bg-critsoft text-crit"}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">{actResult.title}</span>
                <button type="button" className="text-[11.5px] text-muted hover:text-fg" onClick={() => setActResult(null)}>
                  dismiss
                </button>
              </div>
              <pre className="mt-1 max-h-56 overflow-auto font-mono text-[11px] whitespace-pre-wrap">
                {typeof actResult.body === "string" ? actResult.body : JSON.stringify(actResult.body, null, 1)}
              </pre>
            </div>
          )}
        </div>
      </Section>

      <ConfirmDialog
        open={!!act}
        onOpenChange={(o) => !o && setAct(null)}
        title={act?.kind === "readmit" ? `Readmit ${act.e.node}?` : `Finish removing ${act?.e.node ?? ""}?`}
        confirmLabel={act?.kind === "readmit" ? "Readmit" : "Run the removal again"}
        destructive={act?.kind === "finish"}
        pending={actBusy}
        onConfirm={() => void runAct()}
        body={
          act?.kind === "readmit" ? (
            <p className="text-[12.5px] leading-relaxed">
              Lifts the tombstone, so {act.e.node}'s agent registers itself again at its next start. Its old certificates stay
              REVOKED — it must enrol again before it can use the peer channel or replication. Refused when more than one
              identity was evicted under this name.
            </p>
          ) : (
            <div className="space-y-2 text-[12.5px] leading-relaxed">
              <p>
                Runs the eviction again from its tombstone; the steps that already took are no-ops, and the proof is re-read
                afterwards. The recorded reason is kept: "{act?.e.reason || "none given"}".
              </p>
              <Toggle
                checked={ackLoss}
                onChange={setAckLoss}
                label="Proceed even where its own copy of shared data cannot be read"
                help="Only right if the node has been down throughout. Files whose only copy is on it are refused regardless."
              />
            </div>
          )
        }
      />
    </div>
  );
}

export {
  IdentityTab,
  MeshTab,
  JoinTab,
  SecretsTab,
  UpdatesTab,
  RecoveryTab,
  HistoryTab,
};

/**
 * POST /cluster/steps/{step}: re-run ONE membership step on the node serving this console. The
 * console's interventions name steps to re-run (install_tinc among them) and the only way to do it
 * was a curl. Every step is idempotent: on a converged node it reports its asserts and changes nothing.
 */
function RunOneStep({ steps }: { steps: Step[] }) {
  const { client } = useAuth();
  const [step, setStep] = React.useState("");
  return (
    <Section title="Re-run one step on this node">
      <div className="space-y-3">
        <SelectField
          label="Step"
          value={step}
          onChange={setStep}
          options={[
            { value: "", label: "— choose a step —" },
            ...steps.map((s) => ({ value: s.name, label: `${s.label} (phase ${s.phase})`, help: s.name })),
          ]}
        />
        <ActionCard
          title={step ? `Run ${step} on this node` : "Run a step on this node"}
          description="Runs the chosen install/membership step on THIS node only and reports every ASSERT line it checked. Steps are idempotent: on a node that is already converged they report state and change nothing; on one that is not, they do the work the step owns (install a package, render a config, restart its daemon)."
          actionLabel="Run step"
          danger="medium"
          unavailable={step ? undefined : "Choose a step first."}
          confirm={{
            title: `Run ${step} on this node?`,
            body: "A step that finds drift fixes it, which can restart the daemon it owns on this node. Other nodes are not touched.",
            confirmLabel: "Run",
          }}
          run={() => client.apiFetch(`/cluster/steps/${encodeURIComponent(step)}`, { method: "POST" })}
          invalidates={[["cluster-requirements"], ["cluster-steps"]]}
        />
      </div>
    </Section>
  );
}
